import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const contactSchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Veuillez entrer une adresse courriel valide"),
  phone: z.string().optional(),
  message: z.string().min(10, "Le message doit contenir au moins 10 caractères"),
});

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Netlify Forms submission
      const formData = new FormData();
      formData.append("form-name", "contact");
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        toast.success("Message envoyé avec succès!", {
          description: "Nous vous répondrons dans les plus brefs délais.",
        });
        reset();
      } else {
        throw new Error("Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez réessayer ou nous appeler directement.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form 
      name="contact" 
      method="POST" 
      data-netlify="true"
      onSubmit={handleSubmit(onSubmit)} 
      className="space-y-6" 
      data-testid="contact-form"
    >
      <input type="hidden" name="form-name" value="contact" />
      
      <div className="space-y-2">
        <Label htmlFor="name" className="text-slate-700">
          Nom complet <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          type="text"
          placeholder="Votre nom"
          {...register("name")}
          className={`bg-white border-stone-200 focus:border-slate-900 ${
            errors.name ? "border-red-500" : ""
          }`}
          data-testid="contact-name-input"
        />
        {errors.name && (
          <p className="text-red-500 text-sm">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-slate-700">
          Courriel <span className="text-red-500">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="votre@courriel.com"
          {...register("email")}
          className={`bg-white border-stone-200 focus:border-slate-900 ${
            errors.email ? "border-red-500" : ""
          }`}
          data-testid="contact-email-input"
        />
        {errors.email && (
          <p className="text-red-500 text-sm">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-slate-700">
          Téléphone
        </Label>
        <Input
          id="phone"
          type="tel"
          placeholder="450-000-0000"
          {...register("phone")}
          className="bg-white border-stone-200 focus:border-slate-900"
          data-testid="contact-phone-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-slate-700">
          Message <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="message"
          placeholder="Comment pouvons-nous vous aider?"
          rows={5}
          {...register("message")}
          className={`bg-white border-stone-200 focus:border-slate-900 resize-none ${
            errors.message ? "border-red-500" : ""
          }`}
          data-testid="contact-message-input"
        />
        {errors.message && (
          <p className="text-red-500 text-sm">{errors.message.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-primary py-6"
        data-testid="contact-submit-button"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Envoi en cours...</span>
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            <span>Envoyer le message</span>
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
